import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Share2 } from 'lucide-react'
import { XIcon, BlueskyIcon, FacebookIcon } from '@/ui/components/common/BrandIcons'
import { useNativeShareSupport } from '@/application/hooks/useNativeShareSupport'
import { ReadIconAction } from './ReadIconAction'

interface ShareButtonsProps {
  /** Article headline, used as the share text/tweet body. */
  title: string
  /** Absolute, canonical-host, localized article URL (see `useLocalizedUrl`). */
  pageUrl: string
}

type ShareNetworkKey = 'facebook' | 'x' | 'bluesky'

interface ShareNetwork {
  key: ShareNetworkKey
  Icon: () => ReactElement
  buildHref: (title: string, pageUrl: string) => string
}

const NETWORKS: ShareNetwork[] = [
  {
    key: 'facebook',
    Icon: FacebookIcon,
    buildHref: (_title, pageUrl) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
  },
  {
    key: 'x',
    Icon: XIcon,
    buildHref: (title, pageUrl) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(title)}`,
  },
  {
    key: 'bluesky',
    Icon: BlueskyIcon,
    // Bluesky's compose intent has no separate url/text params — the target
    // URL is only ever included by folding it into the shared text itself.
    buildHref: (title, pageUrl) =>
      `https://bsky.app/intent/compose?text=${encodeURIComponent(`${title} ${pageUrl}`)}`,
  },
]

/**
 * Share controls for the article header, rendered immediately to the left of
 * `ReadModeToolbar`'s print button.
 *
 * Two mutually exclusive presentations, chosen by `useNativeShareSupport`:
 *  - Touch devices that expose the Web Share API get ONE round icon button
 *    that hands the title/url to the OS share sheet — no point in three
 *    bespoke network buttons when the platform already offers every app the
 *    visitor has.
 *  - Everything else (desktop, mouse-driven) gets three round icon buttons,
 *    one per network, each opening that network's share intent in a new tab.
 *
 * Either way the buttons are icon-only, matching the print button: the name
 * is carried by `aria-label`/`title` and revealed visually only on hover or
 * focus, so a phone header is never crowded with button text.
 */
export function ShareButtons({ title, pageUrl }: ShareButtonsProps) {
  const { t } = useTranslation()
  const canShareNatively = useNativeShareSupport()

  if (canShareNatively) {
    return (
      <ReadIconAction
        label={t('read_mode.share')}
        onClick={() => {
          navigator.share({ title, url: pageUrl }).catch(() => {
            // The visitor dismissing the OS share sheet rejects this promise
            // (AbortError) — same as any other native-share failure, there's
            // nothing useful to surface for it.
          })
        }}
      >
        <Share2 className="h-4 w-4" />
      </ReadIconAction>
    )
  }

  return (
    <div className="flex items-center gap-2 print:hidden shrink-0">
      {NETWORKS.map(({ key, Icon, buildHref }) => (
        <ReadIconAction
          key={key}
          label={t(`read_mode.share_${key}`)}
          href={buildHref(title, pageUrl)}
        >
          <Icon />
        </ReadIconAction>
      ))}
    </div>
  )
}
